
"use client";

import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { createRoot, Root } from 'react-dom/client';
import { PictureInPictureWindow } from './PictureInPicture';
import { CacheProvider } from '@emotion/react';
import createCache from '@emotion/cache';
import { createTheme, ThemeProvider as MuiThemeProvider } from '@mui/material/styles';

// Extend the Window interface to include the Document Picture-in-Picture API
declare global {
  interface Window {
    documentPictureInPicture: {
      requestWindow(options?: { width?: number; height?: number }): Promise<Window>;
    };
  }
}

/**
 * 复制主文档的样式到 PiP 窗口
 * @param pipWindow PiP 窗口实例
 */
function copyStylesToPipWindow(pipWindow: Window) {
  // Copy essential global styles and CSS variables
  const style = pipWindow.document.createElement('style');
  const globalStyles = Array.from(document.styleSheets)
    .map(s => {
      try {
        return Array.from(s.cssRules).map(r => r.cssText).join('\n');
      } catch { return '' }
    })
    .join('\n');
  style.textContent = globalStyles;
  pipWindow.document.head.appendChild(style);

  // Add Google Fonts for Poetsen One
  const fontLink = pipWindow.document.createElement('link');
  fontLink.href = 'https://fonts.googleapis.com/css2?family=Poetsen+One&display=swap';
  fontLink.rel = 'stylesheet';
  pipWindow.document.head.appendChild(fontLink);

  // Set theme attribute
  const themeAttr = document.documentElement.getAttribute('data-theme');
  if (themeAttr) {
    pipWindow.document.documentElement.setAttribute('data-theme', themeAttr);
  }
  // Mirror current class list (e.g., next-themes uses `class="dark"`)
  pipWindow.document.documentElement.className = document.documentElement.className;
};

/**
 * 持续同步主文档的主题相关属性到 PiP 窗口（class 与 data-theme）。
 */
function attachThemeMirror(pipWindow: Window) {
  // 初始同步
  const sync = () => {
    pipWindow.document.documentElement.className = document.documentElement.className;
    const dataTheme = document.documentElement.getAttribute('data-theme');
    if (dataTheme) {
      pipWindow.document.documentElement.setAttribute('data-theme', dataTheme);
    } else {
      pipWindow.document.documentElement.removeAttribute('data-theme');
    }
  };

  sync();

  // 监听 html 元素属性变化（next-themes 切换会改动 class/data-theme）
  const observer = new MutationObserver(sync);
  observer.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ['class', 'data-theme'],
  });

  // 当 PiP 关闭时，清理观察器
  const cleanup = () => observer.disconnect();
  pipWindow.addEventListener('pagehide', cleanup, { once: true });

  return () => {
    observer.disconnect();
    pipWindow.removeEventListener('pagehide', cleanup);
  };
}

// This component now handles the entire setup for the PiP window's content,
// including style and theme providers.
const PipContentWrapper = ({ pipWindow }: { pipWindow: Window }) => {
  // Create a new Emotion cache instance for the PiP window.
  // It's crucial to specify the `container` as the PiP window's head,
  // so that MUI styles are injected there, not in the main document.
  const cache = createCache({
    key: 'mui-pip',
    container: pipWindow.document.head,
  });

  // Force re-computation when PiP window theme toggles (class/data-theme)
  const [themeVersion, setThemeVersion] = useState(0);
  useEffect(() => {
    const target = pipWindow.document.documentElement;
    const observer = new MutationObserver(() => setThemeVersion(v => v + 1));
    observer.observe(target, { attributes: true, attributeFilter: ['class', 'data-theme'] });
    return () => observer.disconnect();
  }, [pipWindow]);

  const getCSSVariable = (variableName: string) => {
    return pipWindow
      ? pipWindow.getComputedStyle(pipWindow.document.documentElement).getPropertyValue(variableName).trim()
      : '';
  };

  const isDark = pipWindow.document.documentElement.classList.contains('dark');

  const muiTheme = useMemo(() => {
    return createTheme({
      palette: {
        mode: isDark ? 'dark' : 'light',
        primary: {
          main: getCSSVariable('--color-primary') || (isDark ? '#f87c5a' : '#e55f33'),
          contrastText: '#ffffff',
        },
        secondary: {
          main: getCSSVariable('--color-info') || (isDark ? '#60a5fa' : '#3b82f6'),
        },
        error: {
          main: getCSSVariable('--color-error') || (isDark ? '#f87171' : '#ef4444'),
        },
        warning: {
          main: getCSSVariable('--color-warning') || (isDark ? '#fbbf24' : '#f59e0b'),
        },
        info: {
          main: getCSSVariable('--color-info') || (isDark ? '#60a5fa' : '#3b82f6'),
        },
        success: {
          main: getCSSVariable('--color-success') || (isDark ? '#34d399' : '#10b981'),
        },
        background: {
          default: getCSSVariable('--color-background') || (isDark ? '#0f172a' : '#f5e8e5'),
          paper: getCSSVariable('--color-card') || (isDark ? '#1e293b' : '#ffffff'),
        },
        text: {
          primary: getCSSVariable('--color-foreground') || (isDark ? '#f1f5f9' : '#1f2937'),
          secondary: getCSSVariable('--color-muted-foreground') || (isDark ? '#94a3b8' : '#6b7280'),
        },
      },
      components: {
        MuiDialog: {
          styleOverrides: {
            paper: {
              backgroundColor: getCSSVariable('--color-card') || (isDark ? '#1e293b' : '#ffffff'),
              color: getCSSVariable('--color-foreground') || (isDark ? '#f1f5f9' : '#1f2937'),
              transition: 'background-color 0.3s ease, color 0.3s ease',
            },
          },
        },
        MuiCard: {
          styleOverrides: {
            root: {
              backgroundColor: getCSSVariable('--color-card') || (isDark ? '#1e293b' : '#ffffff'),
              color: getCSSVariable('--color-foreground') || (isDark ? '#f1f5f9' : '#1f2937'),
              transition: 'background-color 0.3s ease, color 0.3s ease',
            },
          },
        },
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [themeVersion, isDark]);

  return (
    <CacheProvider value={cache}>
      <MuiThemeProvider theme={muiTheme}>
        <PictureInPictureWindow />
      </MuiThemeProvider>
    </CacheProvider>
  );
};

export const usePictureInPicture = () => {
  const [pipWindow, setPipWindow] = useState<Window | null>(null);
  const rootRef = useRef<Root>(null);

  const openPipWindow = async () => {
    try {
      const newPipWindow = await (window as Window).documentPictureInPicture.requestWindow({
        width: 320,
        height: 420,
      });
      copyStylesToPipWindow(newPipWindow);

      // 挂载主题镜像，让 PiP 在主题切换时实时更新样式
      const detachThemeMirror = attachThemeMirror(newPipWindow);

      const container = newPipWindow.document.createElement('div');
      newPipWindow.document.body.appendChild(container);

      rootRef.current = createRoot(container);
      // Pass the pipWindow instance to the wrapper
      rootRef.current.render(<PipContentWrapper pipWindow={newPipWindow} />);
      setPipWindow(newPipWindow);

      newPipWindow.addEventListener('pagehide', () => {
        if (rootRef.current) rootRef.current.unmount();
        // 清理主题镜像
        detachThemeMirror?.();
        setPipWindow(null);
      });

    } catch (error) {
      console.error('Failed to open PiP window:', error);
    }
  };

  const closePipWindow = useCallback(() => {
    if (pipWindow) pipWindow.close();
  }, [pipWindow]);

  return { pipWindow, openPipWindow, closePipWindow };
};
